import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { supabase } from "@/integrations/supabase/client"
import { useOrganization } from "@/contexts/OrganizationContext"
import { useToast } from "@/hooks/use-toast"

export interface EventGalleryFolder {
  id: string
  event_id: string
  name: string
  parent_folder_id: string | null
  organization_id: string
  created_by: string | null
  created_at: string
  updated_at: string
}

export interface EventGalleryImage {
  id: string
  event_id: string
  folder_id: string | null
  name: string
  file_path: string
  file_size: number | null
  mime_type: string | null
  width: number | null
  height: number | null
  organization_id: string
  uploaded_by: string | null
  created_at: string
  updated_at: string
}

// Fetch folders for an event
export const useEventGalleryFolders = (eventId: string, parentFolderId: string | null = null) => {
  const { currentOrganization } = useOrganization()
  
  return useQuery({
    queryKey: ["event-gallery-folders", eventId, parentFolderId, currentOrganization?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("event_gallery_folders")
        .select("*")
        .eq("event_id", eventId)
        .eq("organization_id", currentOrganization?.id)
        .filter("parent_folder_id", parentFolderId === null ? "is" : "eq", parentFolderId)
        .order("created_at", { ascending: false })

      if (error) throw error
      return data as EventGalleryFolder[]
    },
    enabled: !!eventId && !!currentOrganization?.id,
  })
}

// Fetch images for an event/folder
export const useEventGalleryImages = (eventId: string, folderId: string | null = null) => {
  const { currentOrganization } = useOrganization()
  
  return useQuery({
    queryKey: ["event-gallery-images", eventId, folderId, currentOrganization?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("event_gallery_images")
        .select("*")
        .eq("event_id", eventId)
        .eq("organization_id", currentOrganization?.id)
        .filter("folder_id", folderId === null ? "is" : "eq", folderId)
        .order("created_at", { ascending: false })

      if (error) throw error
      return data as EventGalleryImage[]
    },
    enabled: !!eventId && !!currentOrganization?.id,
  })
}

// Create folder mutation
export const useCreateFolder = () => {
  const queryClient = useQueryClient()
  const { currentOrganization } = useOrganization()
  const { toast } = useToast()

  return useMutation({
    mutationFn: async (data: {
      eventId: string
      name: string
      parentFolderId: string | null
    }) => {
      const { data: result, error } = await supabase
        .from("event_gallery_folders")
        .insert({
          event_id: data.eventId,
          name: data.name,
          parent_folder_id: data.parentFolderId,
          organization_id: currentOrganization?.id,
        })
        .select()
        .single()

      if (error) throw error
      return result
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["event-gallery-folders", variables.eventId]
      })
      toast({
        title: "Folder created",
        description: "The folder has been created successfully.",
      })
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: `Failed to create folder: ${error.message}`,
        variant: "destructive",
      })
    },
  })
}

// Delete folder mutation
export const useDeleteFolder = () => {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: async (data: { folderId: string; eventId: string }) => {
      const { error } = await supabase
        .from("event_gallery_folders")
        .delete()
        .eq("id", data.folderId)

      if (error) throw error
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["event-gallery-folders", variables.eventId]
      })
      toast({
        title: "Folder deleted",
        description: "The folder has been deleted successfully.",
      })
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: `Failed to delete folder: ${error.message}`,
        variant: "destructive",
      })
    },
  })
}

// Upload image mutation
export const useUploadImage = () => {
  const queryClient = useQueryClient()
  const { currentOrganization } = useOrganization()
  const { toast } = useToast()

  return useMutation({
    mutationFn: async (data: {
      eventId: string
      folderId: string | null
      file: File
      onProgress?: (progress: number) => void
    }) => {
      // Generate unique file path
      const fileExt = data.file.name.split('.').pop()
      const fileName = `${data.eventId}/${data.folderId || 'root'}/${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`

      console.log('Uploading file to path:', fileName)

      // Upload to Supabase Storage
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from("event-gallery")
        .upload(fileName, data.file, {
          cacheControl: "3600",
          upsert: false,
        })

      if (uploadError) {
        console.error('Upload error:', uploadError)
        throw uploadError
      }

      console.log('File uploaded successfully:', uploadData)

      // Create image record in database
      const { data: imageRecord, error: dbError } = await supabase
        .from("event_gallery_images")
        .insert({
          event_id: data.eventId,
          folder_id: data.folderId,
          name: data.file.name,
          file_path: uploadData.path,
          file_size: data.file.size,
          mime_type: data.file.type,
          organization_id: currentOrganization?.id,
        })
        .select()
        .single()

      if (dbError) {
        console.error('Database error:', dbError)
        // Clean up uploaded file if database insert fails
        await supabase.storage.from("event-gallery").remove([uploadData.path])
        throw dbError
      }

      console.log('Database record created:', imageRecord)
      return imageRecord
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["event-gallery-images", variables.eventId, variables.folderId]
      })
      toast({
        title: "Image uploaded",
        description: "The image has been uploaded successfully.",
      })
    },
    onError: (error) => {
      console.error('Upload mutation error:', error)
      toast({
        title: "Error",
        description: `Failed to upload image: ${error.message}`,
        variant: "destructive",
      })
    },
  })
}

// Delete image mutation
export const useDeleteImage = () => {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: async (data: { 
      imageId: string
      filePath: string
      eventId: string
      folderId: string | null
    }) => {
      // Delete from database first
      const { error: dbError } = await supabase
        .from("event_gallery_images")
        .delete()
        .eq("id", data.imageId)

      if (dbError) throw dbError

      // Delete from storage
      const { error: storageError } = await supabase.storage
        .from("event-gallery")
        .remove([data.filePath])

      if (storageError) throw storageError
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["event-gallery-images", variables.eventId, variables.folderId]
      })
      toast({
        title: "Image deleted",
        description: "The image has been deleted successfully.",
      })
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: `Failed to delete image: ${error.message}`,
        variant: "destructive",
      })
    },
  })
}

// Get signed URL for private images with authentication check
export const useImageUrl = (filePath: string) => {
  return useQuery({
    queryKey: ["image-signed-url", filePath],
    queryFn: async () => {
      console.log('Creating signed URL for:', filePath)
      
      // Check if user is authenticated
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) {
        console.error('User not authenticated - cannot access private images')
        throw new Error('Authentication required to access images')
      }

      const { data, error } = await supabase.storage
        .from("event-gallery")
        .createSignedUrl(filePath, 3600) // URL valid for 1 hour

      if (error) {
        console.error('Error creating signed URL:', error)
        throw error
      }
      
      console.log('Signed URL created:', data.signedUrl)
      return data.signedUrl
    },
    enabled: !!filePath,
    staleTime: 1000 * 60 * 50, // Consider fresh for 50 minutes
    gcTime: 1000 * 60 * 60, // Keep in cache for 1 hour
    retry: (failureCount, error) => {
      // Don't retry authentication errors
      if (error.message.includes('Authentication required')) {
        return false
      }
      return failureCount < 3
    },
  })
}

// Legacy function for backward compatibility with authentication check
export const getImageUrl = async (filePath: string) => {
  console.log('Legacy getImageUrl called for:', filePath)
  
  // Check if user is authenticated
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) {
    console.error('User not authenticated - cannot access private images')
    return ''
  }
  
  const { data, error } = await supabase.storage
    .from("event-gallery")
    .createSignedUrl(filePath, 3600)
  
  if (error) {
    console.error('Error creating signed URL:', error)
    return ''
  }
  
  console.log('Legacy signed URL created:', data.signedUrl)
  return data.signedUrl
}
